// src/components/QuoteBuilder.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import DoorConfigurator from './DoorConfigurator';
import SummaryContainer from './SummaryContainer';
import Reviews from "../components/Reviews";
import ContactForm from '../components/ContactForm';
import { getAllUrlParams, calculateSquareFootage } from '../utils/helpers';
import PricingService from '../services/PricingService';
import { v4 as uuidv4 } from 'uuid';
import { saveEstimate } from '../utils/api';
import ActionBar from './ActionBar'; // Import ActionBar
import { formatCurrency } from '../utils/formatCurrency'; // Import the updated utility
const reviews = [
	{
		comment: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique.",
		rating: 5,
		author: "Name Surname",
		position: "Position",
		company: "Company name",
		image: "https://images.unsplash.com/profile-1441298803695-accd94000cac?ixlib=rb-0.3.5&q=80&fm=jpg&crop=faces&cs=tinysrgb&fit=crop&h=64&w=64&s=5a9dc749c43ce5bd60870b129a40902f",
	},
	{
		comment: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique.",
		rating: 5,
		author: "Name Surname",
		position: "Position",
		company: "Company name",
		image: "https://images.unsplash.com/profile-1441298803695-accd94000cac?ixlib=rb-0.3.5&q=80&fm=jpg&crop=faces&cs=tinysrgb&fit=crop&h=64&w=64&s=5a9dc749c43ce5bd60870b129a40902f",
	},
	{
		comment: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique.",
		rating: 5,
		author: "Name Surname",
		position: "Position",
		company: "Company name",
		image: "https://images.unsplash.com/profile-1441298803695-accd94000cac?ixlib=rb-0.3.5&q=80&fm=jpg&crop=faces&cs=tinysrgb&fit=crop&h=64&w=64&s=5a9dc749c43ce5bd60870b129a40902f",
	},
];
// Default template for a new door
const defaultNewDoor = () => ({
	id: uuidv4(),
	doorType: 'Bi-Fold', // Default: no selection
	heightFt: '0',
	widthFt: '0',
	widthIn: '0',
	heightIn: '0',
	slideDirection: '', // Example default: 'left'
	openingDirection: '', // Example default: 'inward'
	pocketDoor: '', // Default: 'No'
	screen: '', // Default: 'No'
	interiorColor: '', // Default: 'White'
	exteriorColorSame: '', // Default: 'Yes'
	exteriorColor: '', // Unused if exteriorColorSame is 'Yes'
	hardwareColor: '', // Default: 'Silver'
});

// StepIndicator Component
function StepIndicator({ currentStep, setCurrentStep }) {
	return (
		<div className="flex items-center justify-start gap-6">
			{currentStep === 2 ? (
				<button
					onClick={() => setCurrentStep(1)}
					className="flex items-center gap-2 font-medium cursor-pointer py-[8px] px-[16px] rounded-[5px] bg-[#F5F1ED] border border-[#B6B6B6] text-[#B6B6B6] text-xl"
					aria-label="Go back to Doors step"
					title="Go back to Doors step"
				>
					01. Doors
				</button>
			) : (

				<div
					className={`flex items-center gap-2 font-medium cursor-pointer py-[8px] px-[16px] rounded-[5px] bg-[#F5F1ED] border border-[#000000] text-[#1D1D1D] text-xl ${currentStep === 1
						? 'border-gray-900  text-gray-900'
						: 'text-gray-400'
						}`}
					aria-current={currentStep === 1 ? 'step' : undefined}
				>
					01. Doors
				</div>
			)}

			<div
				className={`flex items-center gap-2 font-medium  py-[8px] px-[16px] rounded-[5px] bg-[#F5F1ED] border text-xl  ${currentStep === 2
					? 'border border-gray-900  text-gray-900'
					: ' text-[#B6B6B6] border border-[#B6B6B6] '
					}`}
				aria-current={currentStep === 2 ? 'step' : undefined}
			>
				02. Contact
			</div>
		</div>
	);
}

function QuoteBuilder() {
	const navigate = useNavigate();

	const [params, setParams] = useState(() => {
		const initialParams = getAllUrlParams();
		return {
			...initialParams,
			doors: initialParams.doors.map((door) => ({
				...door,
				id: door.id || uuidv4(),
				widthIn: door.widthIn || '0',
				heightIn: door.heightIn || '0',
				slideDirection: door.slideDirection || '',
				openingDirection: door.openingDirection || '',
				pocketDoor: door.pocketDoor || '',
				screen: door.screen || '',
				interiorColor: door.interiorColor || '',
				exteriorColorSame: door.exteriorColorSame || '',
				exteriorColor: door.exteriorColor || '',
				hardwareColor: door.hardwareColor || '',
			})),
		};
	});

	const [doorCosts, setDoorCosts] = useState([]);
	const [totalBaseCost, setTotalBaseCost] = useState(0);
	const [totalSquareFootage, setTotalSquareFootage] = useState(0);
	const [currentStep, setCurrentStep] = useState(1);
	const [contactInfo, setContactInfo] = useState({
		firstName: '',
		lastName: '',
		email: '',
		phone: '',
		address: '',
		city: '',
		postalCode: '',
		country: '',
		timeline: '1-3 Months',
		message: '',
		smsConsent: false,
		marketingConsent: true,
	});
	const [errors, setErrors] = useState({});
	const [hasAttemptedNext, setHasAttemptedNext] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		const updateDoorCosts = async () => {
			const costs = await Promise.all(
				params.doors.map(async (door) => {
					if (!door.doorType) {
						return {
							id: door.id,
							retailCost: 0,
							savings: 0,
							totalCost: 0,
							squareFootage: 0,
						};
					}

					const squareFootage = calculateSquareFootage(door);
					const costData = await PricingService.calculateCost(
						squareFootage,
						door.doorType,
						door.screen === 'Yes'
					);
					return {
						id: door.id,
						retailCost: parseFloat(costData.retailCost),
						savings: parseFloat(costData.savings),
						totalCost: parseFloat(costData.totalCost),
						squareFootage: parseFloat(squareFootage),
					};
				})
			);

			setDoorCosts(costs);

			const totalCost = costs.reduce((sum, door) => sum + door.totalCost, 0);
			const totalSquareFootage = costs.reduce((sum, door) => sum + door.squareFootage, 0);

			setTotalBaseCost(totalCost);
			setTotalSquareFootage(totalSquareFootage);
		};

		updateDoorCosts();
	}, [params.doors]);

	// Scroll to top whenever currentStep changes
	useEffect(() => {
		if (currentStep > 1) {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		} else {
			window.scrollBy({ top: 500, behavior: 'smooth' });
		}
		
	}, [currentStep]);

	const handleNextStep = () => {
		if (currentStep === 1) {
			if (!validateDoorTypes()) return;
			setCurrentStep(2);
		} else {
			handleSubmitQuote();
		}
	};

	const handleContactInfoChange = (e) => {
		const { name, value, type, checked } = e.target;
		setContactInfo((prev) => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value,
		}));
	};

	const validateDoorTypes = () => {
		const invalidDoors = params.doors.filter(
			(door) =>
				!door.doorType ||
				door.doorType === '' ||
				(Number(door.widthFt || 0) + Number(door.widthIn || 0) === 0) ||
				(Number(door.heightFt || 0) + Number(door.heightIn || 0) === 0)
		);
		if (invalidDoors.length > 0) {
			setHasAttemptedNext(true);
			alert('Please select a door type, height, and width for all doors.');
			return false;
		}
		return true;
	};

	const handleSubmitQuote = async () => {
		const newErrors = {};
		const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'postalCode'];

		requiredFields.forEach((field) => {
			if (!contactInfo[field]) {
				newErrors[field] = 'This field is required';
			}
		});

		if (
			contactInfo.email &&
			!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactInfo.email)
		) {
			newErrors.email = 'Please enter a valid email address';
		}

		if (
			contactInfo.phone &&
			!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(contactInfo.phone)
		) {
			newErrors.phone = 'Please enter a valid phone number';
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			alert('Please fix the errors in the form.');
			return;
		}

		setErrors({});
		setIsSubmitting(true);

		try {
			const doorDetails = params.doors
				.map((door, index) => {
					const costData = doorCosts.find((dc) => dc.id === door.id) || {};
					const dimensions = `${door.widthFt}' ${door.widthIn}" x ${door.heightFt}' ${door.heightIn}"`;
					const cost = costData.totalCost || totalBaseCost / params.doors.length;
					const retail = costData.retailCost ? formatCurrency(costData.retailCost) : 'N/A';
					const savings = costData.savings ? formatCurrency(costData.savings) : 'N/A';
					return `Door ${index + 1}:
Type: ${door.doorType}
Dimensions: ${dimensions}
Retail: ${retail}
Savings: ${savings}
Cost: ${formatCurrency(cost)}`;
				})
				.join('\n\n');

			const doorTypes = [...new Set(params.doors.map((door) => door.doorType.toLowerCase()))].join('/');

			const payload = {
				"First Name": contactInfo.firstName,
				"Last Name": contactInfo.lastName,
				"Email": contactInfo.email,
				"Address": contactInfo.address,
				"City": contactInfo.city,
				"Postal / Zip Code": contactInfo.postalCode,
				"Country": contactInfo.country,
				"Phone Number": contactInfo.phone,
				"Message": contactInfo.message,
				"SMS Checkbox": contactInfo.smsConsent,
				"Email Checkbox": contactInfo.marketingConsent,
				"Source": "Quote Builder",
				"Timeline": contactInfo.timeline,
				"Door Details": doorDetails,
				"Door Count": params.doors.length,
				"Total Square Footage": totalSquareFootage,
				"Total Cost": formatCurrency(totalBaseCost),
				"Door Types": doorTypes,
			};

			console.log('Submitting Quote Payload:', payload);

			await saveEstimate(payload);

			console.log('Submission Successful');
			setIsSubmitting(false);

			// Navigate to Thank You page and pass the user's email
			navigate('/thank-you', { state: { email: contactInfo.email } });
		} catch (error) {
			console.error('Submission Failed:', error.message);
			alert('There was an error submitting your quote. Please try again.');
			setIsSubmitting(false);
		}
	};


	const hasInvalidDoors =
		params.doors.some(
			(door) =>
				!door.doorType ||
				door.doorType === '' ||
				(Number(door.widthFt || 0) + Number(door.widthIn || 0) === 0) ||
				(Number(door.heightFt || 0) + Number(door.heightIn || 0) === 0)
		) || params.doors.length === 0;

	const removeDoor = (doorId) => {
		const updatedDoors = params.doors.filter((door) => door.id !== doorId);
		setParams((prevParams) => ({ ...prevParams, doors: updatedDoors }));
	};

	const isBiFoldInvalid = (door) => {
		return (
			!door.openingDirection ||
			!door.slideDirection || 
			!door.screen ||
			!door.interiorColor || 
			((!door.exteriorColorSame || door.exteriorColorSame === 'No') && !door.exteriorColor) || 
			!door.hardwareColor 
		);
	};
	
	const isMultiSlideInvalid = (door) => {
		if (!door.slideDirection) return true; 
		if (door.slideDirection !== 'Middle') {
			if (!door.pocketDoor) return true; 
		}
		if (!door.screen) return true; 
		if (!door.interiorColor) return true; 
		if (!door.hardwareColor) return true;
		if ((!door.exteriorColorSame || door.exteriorColorSame === 'No') && !door.exteriorColor) return true;
	
		return false;
	};
	
	

	const useIsNextStepDisabled = (doors) => {
		return (
			doors.some((door) => {
				if (door.doorType === 'Bi-Fold') {
					return isBiFoldInvalid(door);
				} else if (door.doorType === 'Multi-Slide') {
					return isMultiSlideInvalid(door);
				}
				return true;
			}) || doors.length === 0
		);
	};

	// Determine if "Next Step" should be disabled
	const isNextStepDisabled = useIsNextStepDisabled(params.doors);
	// Determine buttons based on currentStep
	const getActionBarButtons = () => {
		if (currentStep === 1) {
			return [
				{
					label: 'Next Step',
					onClick: handleNextStep,
					primary: true,
					disabled: isNextStepDisabled,
					ariaLabel: hasInvalidDoors
						? 'Please select a door type, height, and width for all doors before proceeding.'
						: 'Next Step',
					title: hasInvalidDoors
						? 'Please select a door type, height, and width for all doors before proceeding.'
						: 'Next Step',
				},
			];
		} else if (currentStep === 2) {
			return [
				{
					label: isSubmitting ? 'Submitting...' : 'Submit Quote',
					onClick: handleSubmitQuote,
					primary: true,
					disabled: isSubmitting,
					ariaLabel: isSubmitting ? 'Submitting...' : 'Submit Quote',
					title: isSubmitting ? 'Submitting...' : 'Submit Quote',
					svg: 'up-right arrow',
				},
				{
					label: 'Edit Doors',
					onClick: () => setCurrentStep(1),
					primary: false,
					disabled: false,
					ariaLabel: 'Edit Doors',
					title: 'Edit Doors',
					svg: 'up-right arrow',
				},

			];
		} else {
			// Default to no buttons if step is not recognized
			return [];
		}
	};

	return (
		<div>
			<Navbar />
			<main className="w-full bg-[#F5F1ED]"> {/* Added pb-24 for ActionBar */}
				<div className='w-full bg-white'>
					<div className=' max-w-[1440px] mx-auto pt-[40px] px-[20px] md:px-[40px] lg:px-[80px] pb-[40px] mb-[40px]'>
						<div className=" p-0 mb-[32px]">
							<h1 className="font-medium leading-normal text-[40px] capitalize line-h text-gray-900 mb-[24px]">Quote Builder</h1>
							<p className="text-[#626262] text-[20px]">Complete these 2 simple steps to get your custom quote</p>
						</div>

						<StepIndicator currentStep={currentStep} setCurrentStep={setCurrentStep} />
					</div>
				</div>
				<div className='max-w-[1440px] mx-auto px-[20px] md:px-[40px] lg:px-[80px]'>
					{currentStep === 1 ? (
						<div className="main-container flex flex-wrap gap-6">
							<div className="w-fit p-0 mb-10 rounded">
								{params.doors.map((door) => (
									<DoorConfigurator
										key={door.id}
										door={door}
										doorId={door.id}
										selectDoorOption={(doorId, option, value) =>
											setParams((prevParams) => {
												const updatedDoors = prevParams.doors.map((d) =>
													d.id === doorId ? { ...d, [option]: value } : d
												);
												return { ...prevParams, doors: updatedDoors };
											})
										}
										removeDoor={removeDoor}
										params={params}
										isQuoteBuilder={true}
										hasAttemptedNext={hasAttemptedNext}
									/>
								))}
								<button
									onClick={() =>
										setParams((prev) => ({
											...prev,
											doors: [...prev.doors, defaultNewDoor()],
										}))
									}
									className="text-gray-800 flex items-center justify-start md:justify-center font-faktum gap-x-4 w-full text-2xl font-medium leading-normal capitalize px-[16px] py-[32px] pr-[100px] md:pr-[16px] bg-white rounded-[10px] text-left md:text-center hover:bg-gray-50 mt-4 focus:outline-none focus:ring-2 focus:ring-[#699357]"
								>
									<span className='block'>+</span> Want to Add Another Door?
								</button>
							</div>

							<SummaryContainer
								params={params}
								doorCosts={doorCosts} // Pass doorCosts
								removeDoor={removeDoor}
								currentStep={currentStep}
								setCurrentStep={setCurrentStep}
								handleNextStep={handleNextStep}
								validateDoorTypes={validateDoorTypes}
								hasInvalidDoors={hasInvalidDoors}
								context="quote"
								openModal={() => { }}
								navigateToQuoteBuilder={() => { }}
							/>
						</div>
					) : currentStep === 2 ? (
						<div className=" main-container flex flex-col md:flex-row gap-6">
							<div className="flex-2 p-0 rounded">
								<div className="bg-white py-8 px-4 rounded-[10px]">
									<h2 className="text-2xl font-medium pt-3 pb-[25px] border-b border-b-[#1D1D1D] capitalize mb-8">Contact Details</h2>
									<form onSubmit={(e) => e.preventDefault()} className="focus-within:outline-none">
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-xl font-normal leading-[32px]">
											<div>
												<label className="block mb-2" htmlFor="firstName">
													First Name*
												</label>
												<input
													type="text"
													id="firstName"
													name="firstName"
													value={contactInfo.firstName}
													onChange={handleContactInfoChange}
													className={`w-full p-2 border border-[#1D1D1D] rounded text-sm ${errors.firstName ? 'border-red-500' : ''
														} focus:outline-none focus:ring-2 focus:ring-blue-500`}
													aria-required="true"
													aria-invalid={errors.firstName ? 'true' : 'false'}
												/>
												{errors.firstName && (
													<span className="text-red-500 text-xs">{errors.firstName}</span>
												)}
											</div>
											<div>
												<label className="block mb-2" htmlFor="lastName">
													Last Name*
												</label>
												<input
													type="text"
													id="lastName"
													name="lastName"
													value={contactInfo.lastName}
													onChange={handleContactInfoChange}
													className={`w-full p-2 border border-[#1D1D1D] rounded text-sm ${errors.lastName ? 'border-red-500' : ''
														} focus:outline-none focus:ring-2 focus:ring-blue-500`}
													aria-required="true"
													aria-invalid={errors.lastName ? 'true' : 'false'}
												/>
												{errors.lastName && (
													<span className="text-red-500 text-xs">{errors.lastName}</span>
												)}
											</div>
										</div>
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
											<div>
												<label className="block mb-2 text-xl font-normal leading-[30px]" htmlFor="email">
													Email*
												</label>
												<input
													type="email"
													id="email"
													name="email"
													value={contactInfo.email}
													onChange={handleContactInfoChange}
													className={`w-full p-2 border border-[#1D1D1D] rounded text-sm ${errors.email ? 'border-red-500' : ''
														} focus:outline-none focus:ring-2 focus:ring-blue-500`}
													aria-required="true"
													aria-invalid={errors.email ? 'true' : 'false'}
												/>
												{errors.email && (
													<span className="text-red-500 text-xs">{errors.email}</span>
												)}
											</div>
											<div>
												<label className="block mb-2 text-xl font-normal leading-[30px]" htmlFor="phone">
													Phone Number*
												</label>
												<input
													type="tel"
													id="phone"
													name="phone"
													value={contactInfo.phone}
													onChange={handleContactInfoChange}
													className={`w-full p-2 border border-[#1D1D1D] rounded text-sm ${errors.phone ? 'border-red-500' : ''
														} focus:outline-none focus:ring-2 focus:ring-blue-500`}
													aria-required="true"
													aria-invalid={errors.phone ? 'true' : 'false'}
												/>
												{errors.phone && (
													<span className="text-red-500 text-xs">{errors.phone}</span>
												)}
											</div>
										</div>
										<div className="mb-6">
											<label className="block mb-2 text-xl font-normal leading-[30px]" htmlFor="address">
												Job Site Address
											</label>
											<input
												type="text"
												id="address"
												name="address"
												value={contactInfo.address}
												onChange={handleContactInfoChange}
												className="w-full p-2 border border-[#1D1D1D] rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
												aria-required="false"
											/>
										</div>
										<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
											<div>
												<label className="block mb-2 text-xl font-normal leading-[30px]" htmlFor="city">
													City
												</label>
												<input
													type="text"
													id="city"
													name="city"
													value={contactInfo.city}
													onChange={handleContactInfoChange}
													className="w-full p-2 border border-[#1D1D1D] rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
													aria-required="false"
												/>
											</div>
											<div>
												<label className="block mb-2 text-xl font-normal leading-[30px]" htmlFor="postalCode">
													Postal / Zip Code*
												</label>
												<input
													type="text"
													id="postalCode"
													name="postalCode"
													value={contactInfo.postalCode}
													onChange={handleContactInfoChange}
													className={`w-full p-2 border border-[#1D1D1D] rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.postalCode ? 'border border-[#1D1D1D]-red-500' : ''
														}`}
													aria-required="true"
													aria-invalid={errors.postalCode ? 'true' : 'false'}
												/>
												{errors.postalCode && (
													<span className="text-red-500 text-xs">{errors.postalCode}</span>
												)}
											</div>
											<div>
												<label className="block mb-2 text-xl font-normal leading-[30px]" htmlFor="country">
													Country
												</label>
												<input
													type="text"
													id="country"
													name="country"
													value={contactInfo.country}
													onChange={handleContactInfoChange}
													className="w-full p-2 border border-[#1D1D1D] rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
													aria-required="false"
												/>
											</div>
										</div>
										<div className="mb-5">
											<label className="block mb-2 text-xl font-normal leading-[30px]" htmlFor="timeline">
												Timeline
											</label>
											<select
												id="timeline"
												name="timeline"
												value={contactInfo.timeline}
												onChange={handleContactInfoChange}
												className="w-full p-2 border border-[#1D1D1D] rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
												aria-required="false"
											>
												<option value="ASAP">As soon as possible</option>
												<option value="1-3 Months">1-3 Months</option>
												<option value="3-6 Months">3-6 Months</option>
												<option value="6+ Months">6+ Months</option>
											</select>
										</div>
										<div className="mb-5">
											<label className="block mb-2 text-xl font-normal leading-[30px]" htmlFor="message">
												Message
											</label>
											<textarea
												id="message"
												name="message"
												value={contactInfo.message}
												onChange={handleContactInfoChange}
												className="w-full p-2 border border-[#1D1D1D] rounded h-[188px] focus:outline-none focus:ring-2 focus:ring-blue-500"
												aria-required="false"
											></textarea>
										</div>
										<div className="space-y-4">
											<label className="inline-flex gap-2 items-start text-xs font-medium" htmlFor="smsConsent">
												<input
													type="checkbox"
													id="smsConsent"
													name="smsConsent"
													checked={contactInfo.smsConsent}
													onChange={handleContactInfoChange}
													className="hidden peer border-1 border-[#1D1D1D]"
													aria-label="SMS Consent"
												/>
												<div className="p-2 bg-[#F5F1ED] rounded-[5px] border border-[#699357] peer-checked:bg-[#699357]"></div>
												<span>I consent to receive SMS messages from GlassHaus, LLC regarding updates and offers</span>
											</label>
											<label className="inline-flex w-full gap-2 items-start text-xs font-medium" htmlFor="marketingConsent">
												<input
													type="checkbox"
													id="marketingConsent"
													name="marketingConsent"
													checked={contactInfo.marketingConsent}
													onChange={handleContactInfoChange}
													className="hidden peer border-1 border-[#1D1D1D]"
													aria-label="Marketing Consent"
												/>
												<div className="p-2 bg-[#F5F1ED] rounded-[5px] border border-[#699357] peer-checked:bg-[#699357]"></div>
												<span>I agree to receive emails from GlassHaus, LLC. I can subscribe at anytime</span>
											</label>
										</div>
									</form>
								</div>
							</div>
							<SummaryContainer
								params={params}
								doorCosts={doorCosts} // Pass doorCosts
								removeDoor={removeDoor}
								currentStep={currentStep}
								setCurrentStep={setCurrentStep}
								handleNextStep={handleNextStep}
								validateDoorTypes={validateDoorTypes}
								hasInvalidDoors={hasInvalidDoors}
								context="quote"
								openModal={() => { }}
								navigateToQuoteBuilder={() => { }}
							/>
						</div>
					) : null}
				</div>
			</main>
			{/* <Reviews reviews={reviews} /> */}
			<ContactForm
				subheading="Have a Question?"
				heading="Contact Us"
				paragraph=" Customize your bi-fold glass doors by selecting size, door type, color, and more. Our easy-to-use calculator gives you an instant estimate, so you know what to expect before you buy. SSjfajshdfiojfaohndofv  aidfj if asoid faossdjf oiaiosf idoaj oida o."
				label="Contact Us"
				link="#"
			></ContactForm>

			{/* Conditionally Render ActionBar */}
			{!hasInvalidDoors && totalBaseCost > 0 && (
				<ActionBar
					totalBaseCost={totalBaseCost}
					buttons={getActionBarButtons()}
				/>
			)}
		</div>
	);
}

export default QuoteBuilder;
